import { useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle, CreditCard, LockKey } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import PublicFooter from "@/components/PublicFooter";
import ServiceCategoryNav from "@/components/ServiceCategoryNav";
import { getServiceBySlug, getServicesByCategory } from "../lib/serviceCatalog";

const CRM_PORTAL_URL =
  process.env.REACT_APP_CRM_PORTAL_URL || "https://portal.comfile.in/w/comfile/portal";

export default function ServicePage() {
  const { slug } = useParams();
  const service = getServiceBySlug(slug);

  useEffect(() => {
    if (!service) return undefined;
    const originalTitle = document.title;
    const descriptionTag = document.querySelector('meta[name="description"]');
    const originalDescription = descriptionTag?.content;
    document.title = `${service.title} | Comfile`;
    if (descriptionTag) descriptionTag.content = service.summary;
    return () => {
      document.title = originalTitle;
      if (descriptionTag && originalDescription) descriptionTag.content = originalDescription;
    };
  }, [service]);

  if (!service) return <Navigate to="/" replace />;

  const Icon = service.icon;
  const relatedServices = getServicesByCategory(service.category).filter(
    (item) => item.slug !== service.slug,
  );

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="service-detail-page">
      <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-x-3 px-4 sm:px-6 min-[960px]:flex-nowrap">
          <Link
            to="/"
            className="order-1 flex shrink-0 items-center py-2"
            data-testid="service-page-brand"
          >
            <img src="/comfile-logo.svg" alt="Comfile" className="h-10 w-32 object-contain object-left" />
          </Link>
          <div className="order-2 ml-auto flex shrink-0 items-center gap-2 py-2 min-[960px]:order-3 min-[960px]:ml-0">
            <a href={CRM_PORTAL_URL} data-testid="service-page-login-link">
              <Button variant="outline" size="sm" className="border-slate-300">Client login</Button>
            </a>
            <Link to="/#contact" data-testid="service-page-contact-link">
              <Button size="sm" className="bg-[#C83D43] text-white hover:bg-[#A82D35]">Talk to us</Button>
            </Link>
          </div>
          <ServiceCategoryNav />
        </div>
      </header>

      <main>
        <section className="border-b border-slate-200 bg-[#FBF7F6]">
          <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-16">
            <Link
              to="/#services"
              className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-[#C83D43]"
              data-testid="service-back-link"
            >
              <ArrowLeft size={16} /> All services
            </Link>
            <div className="mt-8 grid gap-10 lg:grid-cols-[1fr_360px] lg:items-center">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#C83D43]">
                  {service.category}
                </p>
                <h1
                  className="mt-3 max-w-3xl font-heading text-3xl font-extrabold leading-tight tracking-tight text-[#1D3970] sm:text-5xl"
                  data-testid="service-title"
                >
                  {service.title}
                </h1>
                <p className="mt-5 max-w-2xl text-base leading-7 text-slate-600 sm:text-lg">
                  {service.description}
                </p>
                <div className="mt-7 flex flex-wrap gap-3">
                  <Link
                    to={`/?service=${encodeURIComponent(service.slug)}#contact`}
                    data-testid="service-enquiry-link"
                  >
                    <Button size="lg" className="bg-[#C83D43] text-white hover:bg-[#A82D35]">
                      Enquire about this service <ArrowRight size={18} className="ml-2" />
                    </Button>
                  </Link>
                  <a href={CRM_PORTAL_URL} data-testid="service-payment-link">
                    <Button size="lg" variant="outline" className="border-slate-300 bg-white">
                      <CreditCard size={18} className="mr-2" /> Client portal & payments
                    </Button>
                  </a>
                </div>
                <p className="mt-3 flex items-start gap-2 text-xs leading-5 text-slate-500">
                  <LockKey size={15} className="mt-0.5 shrink-0" />
                  Online service payments are available in your secure portal after your service
                  request and fee are confirmed by our team.
                </p>
              </div>
              <div className="hidden border border-slate-200 bg-white p-8 lg:block" aria-hidden="true">
                <span className="flex h-16 w-16 items-center justify-center rounded-md bg-[#F8E9EA] text-[#C83D43]">
                  <Icon size={34} weight="regular" />
                </span>
                <div className="mt-8 space-y-4">
                  <div className="h-2 w-4/5 bg-slate-100" />
                  <div className="h-2 w-full bg-slate-100" />
                  <div className="h-2 w-3/5 bg-slate-100" />
                </div>
                <div className="mt-8 border-t border-slate-200 pt-5 text-sm text-slate-600">
                  Clear steps. Secure document sharing. Progress in your client portal.
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto grid max-w-7xl gap-10 px-4 py-12 sm:px-6 sm:py-16 lg:grid-cols-[1fr_360px]">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#C83D43]">What’s included</p>
            <h2 className="mt-3 font-heading text-2xl font-bold tracking-tight text-[#1D3970] sm:text-3xl">
              A guided process for your requirements.
            </h2>
            <ul className="mt-7 space-y-4" data-testid="service-highlights">
              {service.highlights.map((highlight) => (
                <li key={highlight} className="flex items-start gap-3 text-sm leading-6 text-slate-700">
                  <CheckCircle size={19} weight="fill" className="mt-0.5 shrink-0 text-[#C83D43]" />
                  {highlight}
                </li>
              ))}
            </ul>
            <div className="mt-10 border-l-2 border-[#C83D43] bg-[#FBF7F6] px-5 py-4">
              <h2 className="font-heading font-bold text-[#1D3970]">Who this is for</h2>
              <p className="mt-2 text-sm leading-6 text-slate-600">{service.suitableFor}</p>
            </div>
            {relatedServices.length > 0 && (
              <div className="mt-10">
                <h2 className="font-heading text-xl font-bold text-[#1D3970]">
                  More {service.category} services
                </h2>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  {relatedServices.map((related) => (
                    <Link
                      key={related.slug}
                      to={`/services/${related.slug}`}
                      className="border border-slate-200 p-4 text-sm font-medium text-[#1D3970] transition-colors hover:border-[#C83D43] hover:text-[#C83D43]"
                      data-testid={`related-service-${related.slug}`}
                    >
                      {related.title} <ArrowRight size={15} className="ml-1 inline" />
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          <aside className="h-fit border border-slate-200 bg-white p-6 lg:sticky lg:top-40" data-testid="service-actions">
            <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#C83D43]">Next steps</p>
            <h2 className="mt-2 font-heading text-xl font-bold text-[#1D3970]">Get started with Comfile</h2>
            <p className="mt-3 text-sm leading-6 text-slate-600">
              Tell us what you need. We’ll confirm the scope, documents and applicable fees before
              any work or payment begins.
            </p>
            <Link
              to={`/?service=${encodeURIComponent(service.slug)}#contact`}
              className="mt-5 block"
              data-testid="service-sidebar-enquiry-link"
            >
              <Button className="w-full bg-[#C83D43] text-white hover:bg-[#A82D35]">
                Request a consultation <ArrowRight size={17} className="ml-2" />
              </Button>
            </Link>
            <a href={CRM_PORTAL_URL} className="mt-3 block" data-testid="service-sidebar-payment-link">
              <Button variant="outline" className="w-full border-slate-300">
                <CreditCard size={17} className="mr-2" /> Pay in client portal
              </Button>
            </a>
            <p className="mt-4 border-t border-slate-200 pt-4 text-xs leading-5 text-slate-500">
              Portal payments are for existing clients with an approved service request or invoice.
            </p>
          </aside>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
}
